import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatUnits, parseUnits } from 'viem';

import { Button } from '@/components/ui/button';
import { CopyableHash } from '@/components/ui/copyable-hash';
import {
    ANCHOR_CHAINS,
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import { SPEED_NETWORK_ID } from '@/lib/api/speed';
import type { BridgeCatalog, BridgeQuoteResponse } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import { useBridgeOverride } from '../bridgeOverride';
import { useBridgeQuote } from '../hooks/useBridgeQuote';
import type { DirectDepositState } from '../hooks/useDirectDeposit';
import { normalizeDepositStep, phaseLabel } from '../operationPhase';
import { BRIDGE_LABELS, debridgeNativeFee } from '../services/bridgeFees';
import {
    ANCHOR_DEPOSIT_TOKENS,
    anchorBalanceFor,
    buildDepositSourceOptions,
    defaultAnchorSource,
    HYPER_CORE_ACTIVATION_FEE_USD,
    HYPER_FIRST_FUNDING_MIN_6,
    isAnchorSource,
    validateHyperDepositAmount,
} from '../services/directDeposit';
import type {
    AnchorBalances,
    AnchorDepositSource,
    DepositSource,
    DepositSourceOption,
    HyperDepositRoute,
} from '../services/directDeposit';
import type { CustodialDepositState } from '../types';

import BridgeOverrideButton from './BridgeOverrideButton';
import OperationProgress from './OperationProgress';

/**
 * EOA-direct HyperEVM deposit controls, provided by useDirectDeposit (absent = feature not
 * wired, in which case the form is anchor-only exactly as before).
 */
export interface DirectDepositControls {
    state: DirectDepositState;
    run: (amount6: bigint) => Promise<void>;
    reset: () => void;
    isRunning: boolean;
    /** null while the HYPE gas read is in flight. */
    hypeGasOk: boolean | null;
    /** EOA USDC on HyperEVM (display units); null while loading. */
    balance: number | null;
    /** Same balance in base units. */
    balance6: bigint | null;
    /** Which HyperEVM route applies; null while the read is in flight. */
    route: HyperDepositRoute | null;
    /**
     * The adapter exists but has no HyperCore presence yet, so this funding is the one that
     * opens the account — HyperCore charges a one-time fee for that.
     */
    activationPending: boolean;
}

interface CustodialDepositFormProps {
    prefillAmount?: number;
    depositState: CustodialDepositState;
    onConfirm: (amount: bigint, source: DepositSource) => Promise<void>;
    onCancel: () => void;
    onRetry: () => void;
    onReset: () => void;
    onContinue?: () => Promise<void>;
    canContinue?: boolean;
    /**
     * Cancel a deposit still waiting on the wallet signature. Until then nothing has
     * moved, so the user is never locked into a job they no longer want (or one whose
     * signature keeps failing). Absent means the button stays shut while processing.
     */
    onCancelActive?: () => Promise<void>;
    canCancelActive?: boolean;
    /** The user's stable balances across both anchor chains. */
    anchorBalances: AnchorBalances;
    /** Preferred anchor: lists its tokens first and wins the empty default. */
    anchorChainId?: AnchorChainId;
    /**
     * The connected wallet's NATIVE balance per anchor (CRO, ETH), display
     * units; null or absent while unknown. deBridge's fixed fee is paid in it,
     * so the form can say up front when the wallet cannot cover that fee.
     */
    anchorNativeBalances?: Partial<Record<AnchorChainId, number | null>>;
    tokenDecimals: number;
    /** A Snappy Wallet setup is running; it spends from this same wallet, so hold deposits. */
    isReactivating?: boolean;
    /** A withdrawal (either route) is in flight — the same wallet signs both. */
    otherOperationRunning?: boolean;
    /** HyperEVM as a deposit source; the "From" picker is hidden when absent. */
    directDeposit?: DirectDepositControls;
    /** Reopens onboarding at the step the failure points to (launch voucher gate). */
    onRequestSetup?: () => void;
    /**
     * Display pre-quote for the entered amount and the picked source. When it
     * is supplied the button stays shut until a successful quote for THOSE
     * inputs is in hand, and the bridge it names drives the native-fee warning
     * before anything is started. Absent means no gate and the form behaves
     * exactly as it did before.
     */
    /** The backend's bridge registry, for the testing switch's options; null while unknown. */
    bridgeCatalog?: BridgeCatalog | null;
    quoteDeposit?: (
        amount: bigint,
        source: DepositSource | undefined,
        signal: AbortSignal,
    ) => Promise<BridgeQuoteResponse>;
}

/**
 * The WolfSwap swap that turns a token deBridge refuses into the one it takes on
 * the same chain: the anchor's own stable (the token every bridged route here is
 * built around). Null when the picked token already IS that stable, since then
 * there is nothing to swap into.
 */
export function unsupportedTokenSwapFor(
    source: AnchorDepositSource,
): { href: string; buySymbol: string } | null {
    const token = ANCHOR_DEPOSIT_TOKENS[source];
    const stable = ANCHOR_CHAINS[token.chainId].stable;
    if (token.address.toLowerCase() === stable.address.toLowerCase()) {
        return null;
    }
    const params = new URLSearchParams({
        chainId: String(token.chainId),
        sellToken: token.address,
        buyToken: stable.address,
    });
    return {
        href: `https://wolfswap.app/swap?${params.toString()}`,
        buySymbol: stable.symbol,
    };
}

const STEP_LABELS: Partial<Record<CustodialDepositState['status'], string>> = {
    submitting: 'Starting deposit...',
    awaitingSignature: 'Sign deposit in wallet...',
    bridging: 'Bridging funds...',
};

export const DEPOSIT_PROCESSING_STATUSES = new Set<
    CustodialDepositState['status']
>(['submitting', 'awaitingSignature', 'bridging']);

const PROCESSING_STATUSES = DEPOSIT_PROCESSING_STATUSES;

function withEllipsis(label: string): string {
    const trimmed = label.trim();
    return trimmed.endsWith('...')
        ? trimmed
        : `${trimmed.replace(/\.+$/, '')}...`;
}

const CustodialDepositForm: React.FC<CustodialDepositFormProps> = ({
    prefillAmount,
    depositState,
    onConfirm,
    onCancel,
    onRetry,
    onReset,
    onContinue,
    canContinue = false,
    onCancelActive,
    canCancelActive = false,
    anchorBalances,
    anchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
    anchorNativeBalances,
    tokenDecimals,
    isReactivating = false,
    otherOperationRunning = false,
    directDeposit,
    onRequestSetup,
    bridgeCatalog = null,
    quoteDeposit,
}) => {
    const [inputAmount, setInputAmount] = useState<string>(
        prefillAmount && prefillAmount > 0 ? prefillAmount.toString() : '',
    );
    const [source, setSource] = useState<DepositSource>(
        defaultAnchorSource(anchorChainId),
    );
    // once the user picks a chain themselves, an arriving balance/gas read must not move it
    const [sourceTouched, setSourceTouched] = useState(false);

    useEffect(() => {
        if (prefillAmount && prefillAmount > 0) {
            setInputAmount(prefillAmount.toString());
        }
    }, [prefillAmount]);

    const {
        status,
        error,
        txHash,
        needsOnboarding,
        onboardingStep,
        progressMessage,
        progressStep,
    } = depositState;
    const needsVoucher = needsOnboarding && onboardingStep === 'voucher';

    const direct = directDeposit;
    const isProcessing = PROCESSING_STATUSES.has(status) || !!direct?.isRunning;
    // a job waiting on the wallet signature has moved nothing yet, so it may be walked away from
    const canCancelWaitingDeposit =
        status === 'awaitingSignature' && canCancelActive && !!onCancelActive;

    // one scan builds the picker rows AND the preselect they imply — see
    // buildDepositSourceOptions for the listing and defaulting rules.
    const { rows: pickerOptions, defaultSource } = useMemo(
        () =>
            buildDepositSourceOptions({
                anchorChainId,
                anchorBalances,
                hyperBalance: direct?.balance ?? null,
                hypeGasOk: direct?.hypeGasOk ?? null,
                hyperAvailable: !!direct,
            }),
        [anchorBalances, anchorChainId, direct],
    );

    useEffect(() => {
        if (sourceTouched || isProcessing) return;
        setSource(defaultSource);
    }, [defaultSource, isProcessing, sourceTouched]);

    const isHyper = source === 'hyperEvm' && !!direct;
    const anchorSource = isAnchorSource(source)
        ? source
        : defaultAnchorSource(anchorChainId);
    const anchorBalance = anchorBalanceFor(anchorBalances, anchorSource);
    const anchorSymbol = ANCHOR_DEPOSIT_TOKENS[anchorSource].symbol;
    // the SELECTED token's own chain, not a session-wide anchor: the source
    // picker spans both anchor chains and the copy has to follow the pick
    const anchorSourceChainId = ANCHOR_DEPOSIT_TOKENS[anchorSource].chainId;
    const anchorName = ANCHOR_CHAINS[anchorSourceChainId].label;
    const sourceBalance = isHyper ? (direct?.balance ?? null) : anchorBalance;
    // The backend falls back to deBridge when Relay has the origin chain
    // disabled, and on this route the user's OWN wallet pays deBridge's fixed
    // native order fee on top of gas. Saying so is the difference between a
    // deposit that goes through and one that fails for a few coins.
    const bridgeNativeFee = debridgeNativeFee(anchorSourceChainId);
    // Worth showing whenever there is more than one row — which now includes the
    // all-empty case, where seeing WHICH tokens are accepted (and that you hold
    // none of them) is the whole reason to render it.
    const showSourcePicker = pickerOptions.length > 1;

    const numericAmount = parseFloat(inputAmount) || 0;
    const isValidAmount = numericAmount > 0;
    const exceedsEoa = anchorBalance !== null && numericAmount > anchorBalance;

    /** The typed amount in base units; 0n when it is not a usable number. */
    const amount6 = (() => {
        if (!isValidAmount) return 0n;
        try {
            return parseUnits(inputAmount.trim(), tokenDecimals);
        } catch {
            return parseUnits(
                numericAmount.toFixed(tokenDecimals),
                tokenDecimals,
            );
        }
    })();

    // HyperCore holds back a one-time $1 fee for a fresh account's first transfer out, so a
    // first deposit has a floor the ordinary route does not.
    const requiresActivationMinimum =
        isHyper && direct!.route === 'selfFund' && direct!.activationPending;
    const hyperBlocker = isHyper
        ? validateHyperDepositAmount({
              amount6,
              balance6: direct!.balance6,
              requiresActivationMinimum,
          })
        : null;

    const exceedsBalance = isHyper
        ? hyperBlocker === 'exceedsBalance'
        : exceedsEoa;
    const belowActivationMinimum = hyperBlocker === 'belowActivationMinimum';
    const amountError = exceedsBalance
        ? isHyper
            ? 'Exceeds your HyperEVM balance'
            : 'Exceeds wallet balance'
        : belowActivationMinimum
          ? `First deposit must be at least $${formatUnits(
                HYPER_FIRST_FUNDING_MIN_6,
                tokenDecimals,
            )}`
          : null;

    // --- what this deposit actually costs, asked before the button unlocks ---
    //
    // Priced per SOURCE as well as per amount: a HyperEVM-origin deposit crosses
    // no bridge (the backend answers ok immediately), an anchor-origin one is
    // carried by whichever bridge is live, and the two cost nothing alike.
    const quoteSource: DepositSource | undefined = isHyper
        ? 'hyperEvm'
        : anchorSource;
    const quoteAmount6 =
        quoteDeposit && isValidAmount && !amountError && amount6 > 0n
            ? amount6
            : null;
    // the pinned bridge (testing switch) is in the key too: flipping it must re-price
    const bridgeOverride = useBridgeOverride((s) => s.bridge);
    const quoteKey =
        quoteAmount6 === null
            ? null
            : `${quoteSource}|${quoteAmount6}|${bridgeOverride}`;
    const runQuote = useCallback(
        async (signal: AbortSignal) =>
            quoteDeposit!(quoteAmount6!, quoteSource, signal),
        [quoteAmount6, quoteDeposit, quoteSource],
    );
    const priceQuote = useBridgeQuote({ key: quoteKey, fetch: runQuote });
    const quoted = priceQuote.quote;
    /** A price has to be in hand before this amount may be sent. */
    const quoteGates = quoteKey !== null;

    // The pre-quote names the bridge before anything starts; the started
    // deposit's own state wins once it exists, because by then the backend has
    // re-quoted and this is the route the money is actually taking.
    const effectiveBridge = depositState.bridge ?? quoted?.bridge ?? null;
    const usesDeBridge = effectiveBridge === 'debridge';
    // the bridge's cut in dollars, the started deposit's own figure first
    const effectiveFeeUsd =
        (typeof depositState.bridgeFeeUsd === 'number'
            ? depositState.bridgeFeeUsd
            : null) ??
        quoted?.bridgeFeeUsd ??
        null;
    // deBridge's fixed fee is NOT taken from the stable being deposited: the
    // wallet pays it in the chain's native currency, so it is shown as its own
    // line and checked against what the wallet holds.
    const nativeFee = usesDeBridge && !isHyper ? bridgeNativeFee : null;
    const nativeBalance =
        nativeFee !== null
            ? (anchorNativeBalances?.[anchorSourceChainId] ?? null)
            : null;
    const nativeFeeShort =
        nativeFee !== null &&
        nativeBalance !== null &&
        nativeBalance < nativeFee.amount;
    const estimatedReceive =
        (typeof depositState.estimatedReceive === 'number' &&
        depositState.estimatedReceive > 0
            ? depositState.estimatedReceive
            : null) ??
        quoted?.estimatedReceive ??
        null;
    // Relay takes every token the picker lists; deBridge, the failover, does not
    // (Cronos native USDC, 2026-09-02). When Relay is down and the picked token
    // is one deBridge refuses, the token stays selectable but the button stays
    // shut, and the way out is a swap into the token deBridge does take, on
    // WolfSwap with both sides pre-filled.
    const unsupportedTokenSwap =
        priceQuote.reasonCode === 'bridgeTokenUnsupported' && !isHyper
            ? unsupportedTokenSwapFor(anchorSource)
            : null;
    // a disabled button with nothing beside it reads as broken: say whether it
    // is still pricing or priced and refused.
    const quoteNotice: {
        text: string;
        blocking: boolean;
        link?: { href: string; label: string };
    } | null = !quoteGates
        ? null
        : unsupportedTokenSwap
          ? {
                text:
                    `${priceQuote.reason ?? `No bridge can take ${anchorSymbol} on ${anchorName} directly right now.`} ` +
                    `Swap it to ${unsupportedTokenSwap.buySymbol} on WolfSwap first, then deposit that.`,
                blocking: true,
                link: {
                    href: unsupportedTokenSwap.href,
                    label: `Swap ${anchorSymbol} to ${unsupportedTokenSwap.buySymbol} on WolfSwap`,
                },
            }
          : priceQuote.reason
            ? { text: priceQuote.reason, blocking: true }
            : priceQuote.ready
              ? null
              : { text: 'Checking the current bridge fee...', blocking: false };

    // the deposit and withdraw sequences share step names (`bridging` is in both, at different
    // positions), so the direction decides which sequence the position is counted against.
    // the custodial-balance move reports a generic `bridging` on its way to the funding stage
    const effectiveStep = normalizeDepositStep(
        progressStep,
        depositState.route,
    );
    const phase = phaseLabel(effectiveStep, 'deposit');

    const handleConfirm = () => {
        if (isHyper) {
            if (hyperBlocker || !direct!.route) return;
            // adapter already deployed → fund it directly; otherwise the backend saga
            // onboards this user server-side and asks for a plain transfer.
            if (direct!.route === 'selfFund') {
                void direct!.run(amount6);
                return;
            }
            void onConfirm(amount6, 'hyperEvm');
            return;
        }
        if (!isValidAmount || exceedsEoa) return;
        const amountBigInt = parseUnits(inputAmount, tokenDecimals);
        void onConfirm(amountBigInt, anchorSource);
    };

    const handleMax = () => {
        if (sourceBalance !== null && sourceBalance > 0) {
            setInputAmount(sourceBalance.toString());
        }
    };

    if (status === 'success') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-success/30 bg-success/10">
                    <span className="text-2xl text-success" aria-hidden="true">
                        ✓
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="status"
                        className="text-sm font-semibold text-success"
                    >
                        Deposit confirmed
                    </p>
                    {txHash && <CopyableHash value={txHash} className="mt-2" />}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onReset}
                    className="mt-2"
                >
                    Done
                </Button>
            </div>
        );
    }

    if (status === 'failed') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div
                    className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-lg border',
                        needsOnboarding
                            ? 'border-amber-500/30 bg-amber-500/10'
                            : 'border-destructive/30 bg-destructive/10',
                    )}
                >
                    <span
                        aria-hidden="true"
                        className={cn(
                            'text-2xl',
                            needsOnboarding
                                ? 'text-amber-400'
                                : 'text-destructive',
                        )}
                    >
                        {needsOnboarding ? '⚠' : '✗'}
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="alert"
                        className={cn(
                            'text-sm font-semibold',
                            needsOnboarding
                                ? 'text-amber-300'
                                : 'text-destructive',
                        )}
                    >
                        {needsOnboarding
                            ? 'Custodial setup required'
                            : 'Deposit failed'}
                    </p>
                    {error && (
                        <p className="mt-1 max-w-[280px] text-[10px] text-muted-foreground">
                            {error}
                        </p>
                    )}
                    {needsOnboarding && (
                        <p className="mt-2 max-w-[280px] text-[10px] leading-relaxed text-muted-foreground">
                            {needsVoucher
                                ? 'Snappy Wallet setup is limited during launch. Enter your launch code and wait for your turn, then deposit.'
                                : 'Quick trade uses a gasless custodial wallet. Sign in above to create it automatically, then deposit, no extra steps.'}
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>
                        Dismiss
                    </Button>
                    {needsOnboarding && onRequestSetup ? (
                        <Button size="sm" onClick={onRequestSetup}>
                            {needsVoucher
                                ? 'Get launch access'
                                : 'Finish setup'}
                        </Button>
                    ) : (
                        <Button size="sm" onClick={onRetry}>
                            Retry
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    if (direct?.state.status === 'success') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-success/30 bg-success/10">
                    <span className="text-2xl text-success" aria-hidden="true">
                        ✓
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="status"
                        className="text-sm font-semibold text-success"
                    >
                        Deposit confirmed
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                        {direct.state.depositedUsd !== null
                            ? `$${direct.state.depositedUsd.toFixed(2)} funded from your wallet on HyperEVM`
                            : 'Funded from your wallet on HyperEVM'}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={direct.reset}
                    className="mt-2"
                >
                    Done
                </Button>
            </div>
        );
    }

    if (direct?.state.status === 'failed') {
        return (
            <div className="flex flex-col items-center gap-4 py-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/10">
                    <span
                        className="text-2xl text-destructive"
                        aria-hidden="true"
                    >
                        ✗
                    </span>
                </div>
                <div className="text-center">
                    <p
                        role="alert"
                        className="text-sm font-semibold text-destructive"
                    >
                        Deposit failed
                    </p>
                    {direct.state.error && (
                        <p className="mt-1 max-w-[280px] text-[10px] text-muted-foreground">
                            {direct.state.error}
                        </p>
                    )}
                </div>
                <Button variant="outline" size="sm" onClick={direct.reset}>
                    Dismiss
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {showSourcePicker && (
                <div>
                    <label
                        htmlFor="deposit-source"
                        className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                        From
                    </label>
                    <select
                        id="deposit-source"
                        value={source}
                        onChange={(event) => {
                            setSourceTouched(true);
                            setSource(event.target.value as DepositSource);
                        }}
                        disabled={isProcessing || otherOperationRunning}
                        className="h-10 w-full rounded-lg border border-muted-foreground/70 bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
                    >
                        {pickerOptions.map((option: DepositSourceOption) => (
                            <option
                                key={option.source}
                                value={option.source}
                                disabled={!option.enabled}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {isHyper && (
                <p className="text-[10px] text-muted-foreground">
                    Depositing sends USDC you hold on HyperEVM and uses HYPE for
                    gas.
                </p>
            )}

            {isHyper && requiresActivationMinimum && (
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                    {`HyperCore charges a one-time $${HYPER_CORE_ACTIVATION_FEE_USD.toFixed(
                        2,
                    )} fee on your first transfer out of your trading account. It is kept in reserve and not available to withdraw.`}
                </p>
            )}

            <div>
                <label
                    htmlFor="deposit-amount"
                    className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                >
                    Amount (USDC)
                </label>
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">
                            $
                        </span>
                        <input
                            id="deposit-amount"
                            type="number"
                            value={inputAmount}
                            onChange={(e) => setInputAmount(e.target.value)}
                            placeholder="0.00"
                            disabled={isProcessing}
                            aria-invalid={!!amountError || undefined}
                            aria-describedby={
                                amountError ? 'deposit-amount-error' : undefined
                            }
                            className="h-10 w-full rounded-lg border border-muted-foreground/70 bg-background pl-6 pr-3 text-right font-mono font-bold tabular-nums text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleMax}
                        disabled={isProcessing || !sourceBalance}
                        className="h-10 text-xs font-semibold"
                    >
                        Max
                    </Button>
                </div>
                {sourceBalance !== null && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                        {isHyper
                            ? 'Wallet (HyperEVM): '
                            : `Wallet (${anchorSymbol}): `}
                        <span className="font-mono font-semibold tabular-nums text-foreground">
                            ${sourceBalance.toFixed(2)}
                        </span>
                    </p>
                )}
                {(estimatedReceive !== null ||
                    effectiveFeeUsd !== null ||
                    nativeFee !== null) && (
                    // the same box the withdraw form uses: what lands, then every
                    // cost named with its bridge, so the deposited figure and the
                    // trading balance that follows it are never a surprise.
                    <div className="mt-2 rounded-lg border border-border bg-card/50 px-3 py-2">
                        {estimatedReceive !== null && (
                            <p className="text-xs text-foreground">
                                {`You'll receive ≈ $${estimatedReceive.toFixed(2)}`}
                            </p>
                        )}
                        {effectiveFeeUsd !== null && (
                            <p className="mt-1 text-[10px] text-muted-foreground">
                                {`${effectiveBridge ? BRIDGE_LABELS[effectiveBridge] : 'Bridge'} fee ≈ $${effectiveFeeUsd.toFixed(2)}`}
                            </p>
                        )}
                        {nativeFee !== null && (
                            <p
                                className={
                                    nativeFeeShort
                                        ? 'mt-1 text-[10px] text-destructive'
                                        : 'mt-1 text-[10px] text-muted-foreground'
                                }
                            >
                                {`${BRIDGE_LABELS.debridge} network fee: about ${nativeFee.label}`}
                            </p>
                        )}
                    </div>
                )}
                {nativeFee !== null && nativeFeeShort && (
                    <div
                        role="alert"
                        className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2"
                    >
                        <p className="text-[10px] text-amber-400">
                            {`You don't have enough ${nativeFee.symbol} on ${anchorName} to cover the ${BRIDGE_LABELS.debridge} fee. About ${nativeFee.label} is needed, plus gas.`}
                        </p>
                    </div>
                )}
                {quoteNotice && !amountError && (
                    <p
                        role={quoteNotice.blocking ? 'alert' : 'status'}
                        className={
                            quoteNotice.blocking
                                ? 'mt-1 text-[10px] text-destructive'
                                : 'mt-1 text-[10px] text-muted-foreground'
                        }
                    >
                        {quoteNotice.text}
                        {quoteNotice.link && (
                            <>
                                {' '}
                                <a
                                    href={quoteNotice.link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                                >
                                    {quoteNotice.link.label}
                                </a>
                            </>
                        )}
                    </p>
                )}
                {amountError && (
                    <p
                        id="deposit-amount-error"
                        role="alert"
                        className="mt-1 text-[10px] text-destructive"
                    >
                        {amountError}
                    </p>
                )}
            </div>

            {isHyper && direct!.hypeGasOk === false && !isProcessing && (
                <p
                    role="alert"
                    className="text-[11px] leading-relaxed text-amber-400"
                >
                    Your wallet needs a little HYPE on HyperEVM to pay gas for
                    the direct deposit.
                </p>
            )}

            {direct?.isRunning ? (
                <div
                    role="status"
                    className="rounded-lg border border-border bg-secondary/40 px-3 py-2"
                >
                    <div className="flex items-center gap-2">
                        <Loader2
                            size={14}
                            className="animate-spin text-muted-foreground"
                        />
                        <span className="text-xs text-muted-foreground">
                            {withEllipsis(
                                direct.state.stepLabel ?? 'Preparing deposit',
                            )}
                        </span>
                        {direct.state.stepCount > 0 && (
                            <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
                                {`Step ${direct.state.stepIndex} of ${direct.state.stepCount}`}
                            </span>
                        )}
                    </div>
                    <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground/70">
                        Confirm each transaction in your wallet, every step is
                        signed by you.
                    </p>
                </div>
            ) : (
                isProcessing && (
                    <div
                        role="status"
                        className="rounded-lg border border-border bg-secondary/40 px-3 py-2"
                    >
                        <div className="flex items-center gap-2">
                            <Loader2
                                size={14}
                                className="animate-spin text-muted-foreground"
                            />
                            <span className="text-xs text-muted-foreground">
                                {withEllipsis(
                                    phase?.label ??
                                        progressMessage ??
                                        STEP_LABELS[status] ??
                                        'Processing...',
                                )}
                            </span>
                            {phase?.index !== undefined && (
                                // a deposit runs several block-length waits back to back;
                                // without the position each restarting bar reads as a loop
                                <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground/70">
                                    {`Step ${phase.index} of ${phase.total}`}
                                </span>
                            )}
                        </div>
                        {/* time-based, per phase. The backend's own `progress` is a coarse saga
                            percentage that sits still while a step waits on a block. */}
                        <OperationProgress
                            direction="deposit"
                            step={effectiveStep}
                            chainId={depositState.originChainId ?? null}
                            className="mt-2"
                        />
                        {status === 'awaitingSignature' &&
                            canContinue &&
                            onContinue && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void onContinue()}
                                    className="mt-3 h-8 w-full border-primary/30 text-[10px] text-primary hover:border-primary/60 hover:bg-primary/10"
                                >
                                    Continue...
                                </Button>
                            )}
                    </div>
                )
            )}

            {isReactivating && (
                <p className="mb-2 text-[11px] leading-relaxed text-amber-400">
                    Snappy Wallet setup is running, you can deposit again once
                    it finishes.
                </p>
            )}
            {otherOperationRunning && !isProcessing && (
                <p className="mb-2 text-[11px] leading-relaxed text-amber-400">
                    A withdrawal is in progress, you can deposit again once it
                    finishes.
                </p>
            )}
            <div className="flex items-center gap-2">
                {isAnchorSource(source) && (
                    <BridgeOverrideButton
                        disabled={isProcessing}
                        catalog={bridgeCatalog}
                        originChainId={anchorSourceChainId}
                        destinationChainId={SPEED_NETWORK_ID}
                        leg="userDeposit"
                    />
                )}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={
                        canCancelWaitingDeposit
                            ? () => void onCancelActive()
                            : onCancel
                    }
                    disabled={isProcessing && !canCancelWaitingDeposit}
                    className="flex-1"
                >
                    {canCancelWaitingDeposit ? 'Cancel deposit' : 'Cancel'}
                </Button>
                <Button
                    size="sm"
                    onClick={handleConfirm}
                    disabled={
                        !isValidAmount ||
                        isProcessing ||
                        isReactivating ||
                        otherOperationRunning ||
                        // no price in hand for THIS amount and source, no send
                        (quoteGates && !priceQuote.ready) ||
                        (isHyper
                            ? !!hyperBlocker ||
                              !direct!.route ||
                              direct!.hypeGasOk === false
                            : exceedsEoa)
                    }
                    className="flex-1"
                >
                    {isProcessing ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        'Deposit'
                    )}
                </Button>
            </div>
        </div>
    );
};

export default CustodialDepositForm;
